import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";


import { useNavigate } from "react-router-dom";
import DashboardLayout from "../layout/DashboardLayout";

import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

// ✅ Uses same events datasheet style as your app (optional for now)
// If you want to swap to backend later, keep UI same.
import { upcomingEventsData } from "../data/upcomingEventsData";

function fmtDateShort(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function fmtTime(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const mockBanners = [
  { id: "b1", title: "Art Classes", image: Banner1 },
  { id: "b2", title: "Weekend Events", image: Banner2 }
];

export default function Bookings() {
  const navigate = useNavigate();
  const [view, setView] = useState("grid");

  // ✅ For now, derive bookings from upcomingEventsData so UI looks real.
  // Later: replace with API call to bookings endpoint.
  const { upcomingBookings, pastBookings } = useMemo(() => {
    const list = Array.isArray(upcomingEventsData) ? [...upcomingEventsData] : [];

    // create a nice demo split
    const upcoming = list.slice(0, 4).map((ev, idx) => ({
      id: `up_${ev.id}`,
      tag: ev.category || "Workshop",
      status: idx === 3 ? "Pending" : "Confirmed",
      statusTone: idx === 3 ? "warning" : "success",
      title: ev.title,
      date: ev.dateTime,
      coverImage: ev.coverImage
    }));

    const past = list.slice(4, 8).map((ev) => ({
      id: `past_${ev.id}`,
      tag: ev.category || "Workshop",
      title: ev.title,
      date: ev.dateTime,
      coverImage: ev.coverImage
    }));

    return { upcomingBookings: upcoming, pastBookings: past };
  }, []);

  const isList = view === "list";
  const upcomingCols = isList ? "repeat(1, minmax(0, 1fr))" : { xs: "1fr", md: "repeat(4, 1fr)" };
  const pastCols = isList ? "repeat(1, minmax(0, 1fr))" : { xs: "1fr", md: "repeat(4, 1fr)" };

  return (
    <DashboardLayout title="Dashboard">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 3 }}>
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Header strip (same style as Events/Home) */}
          <Card sx={{ bgcolor: "action.selected" }}>
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ fontWeight: 900 }}>Your Booking&apos;s</Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary", lineHeight: 1.6 }}>
                Your upcoming and past event registrations at NeherBaari. Review your scheduled experiences, add them to your
                calendar, and revisit memorable moments from your journey with us.
              </Typography>
            </CardContent>
          </Card>

          {/* Upcoming Bookings */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Upcoming Bookings</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 1.5
              }}
            >
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_, v) => v && setView(v)}
                size="small"
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}
              >
                <ToggleButton value="grid" sx={{ px: 1.1 }}>
                  <GridViewRoundedIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list" sx={{ px: 1.1 }}>
                  <ViewListRoundedIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: upcomingCols, gap: 2 }}>
              {upcomingBookings.map((b) => (
                <Card key={b.id} sx={{ overflow: "hidden" }}>
                  <Box
                    sx={{
                      height: isList ? 150 : 150,
                      backgroundImage: `url(${b.coverImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />

                  <CardContent sx={{ pt: 2, pb: 2.2 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      <Chip size="small" label={b.tag} />
                      <Chip
                        size="small"
                        label={b.status}
                        color={b.statusTone}
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                    </Box>

                    <Typography sx={{ mt: 1.2, fontSize: 13.5, fontWeight: 900, color: "text.primary" }}>
                      {b.title}
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mt: 0.9 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <CalendarMonthOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.8 }} />
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                          {fmtDateShort(b.date)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <AccessTimeOutlinedIcon fontSize="small" sx={{ color: "text.secondary", opacity: 0.8 }} />
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                          {fmtTime(b.date)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, mt: 1.3, flexWrap: "wrap" }}>
                      <Typography
                        onClick={() => navigate(`/events/${String(b.id).replace("up_", "")}`)}
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: "text.secondary",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" }
                        }}
                      >
                        View Details
                      </Typography>

                      <Typography
                        onClick={() => alert("Calendar integration can be added next.")}
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 900,
                          color: "primary.main",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" }
                        }}
                      >
                        Add to Calendar
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>


          {/* Quick Actions */}

{/* Quick Actions – Match Home Page Style */}
<Box sx={{ mt: 4 }}>
  <Box className="sectionHeader">
    <Typography className="sectionHeaderTitle">Quick Actions</Typography>
    <Box className="sectionHeaderLine" />
  </Box>

  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
      gap: 2,
      mt: 2
    }}
  >
    {[
      {
        title: "Explore Events",
        subtitle: "Browse upcoming activities",
        icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 36, color: "#c28f2c" }} />,
        path: "/events"
      },
      {
        title: "My Bookings",
        subtitle: "View your reservations",
        icon: <BookmarkRoundedIcon sx={{ fontSize: 36, color: "#c28f2c" }} />,
        path: "/bookings"
      },
      {
        title: "Membership",
        subtitle: "Manage your plan",
        icon: <SettingsRoundedIcon sx={{ fontSize: 36, color: "#c28f2c" }} />,
        path: "/settings"
      }
    ].map((item) => (
      <Card
        key={item.title}
        elevation={0}
        onClick={() => navigate(item.path)}
        sx={{
          cursor: "pointer",
          borderRadius: 3,
          p: 3,
          textAlign: "center",
          transition: "0.2s",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        <Box sx={{ mb: 1 }}>{item.icon}</Box>

        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
          {item.title}
        </Typography>

        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.7 }}>
          {item.subtitle}
        </Typography>
      </Card>
    ))}
  </Box>
</Box>



        </Box>

        {/* RIGHT banners (same as other pages) */}
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            gap: 2
          }}
        >
          {mockBanners.map((b) => (
            <Card
              key={b.id}
              elevation={0}
              sx={{
                overflow: "hidden",
                bgcolor: "background.paper"
              }}
            >
              <Box sx={{ p: 1.5, bgcolor: "action.hover" }}>
                <Box
                  sx={{
                    height: 320,
                    borderRadius: 0,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    backgroundImage: `url(${b.image})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain"
                  }}
                />
              </Box>

              <Box sx={{ p: 1.5, pt: 1.2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate("/events")}
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    fontWeight: 800,
                    borderRadius: 14,
                    py: 1
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
